import React, { useEffect, useState, createElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Copy, Check, AlertTriangle, ExternalLink, Info } from 'lucide-react';
import { formConfigsApi, signupFormsApi, emailTemplatesApi, SignupFormRecord, EmailTemplateRecord, FormConfigRecord } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
import { refreshFormEmbedCodes } from './SignupFormEditor';
// --- Helpers ---
function extractLookupId(val: any): string {
  if (!val) return '';
  if (typeof val === 'object' && val.ID) return String(val.ID);
  if (typeof val === 'number') return String(val);
  if (typeof val === 'string') return val;
  return '';
}
function extractFormFields(html: string): string[] {
  const fields: string[] = [];
  const regex = /(?:name|id)=["']([^"']+)["']/gi;
  let match;
  while ((match = regex.exec(html)) !== null) {
    const name = match[1];
    if (name && !fields.includes(name) && !['submit', 'button'].includes(name.toLowerCase())) {
      fields.push(name);
    }
  }
  return fields;
}
function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text);
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '-9999px';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  return new Promise((resolve, reject) => {
    document.execCommand('copy') ? resolve() : reject();
    textarea.remove();
  });
}
export function FormConfiguration() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  // Derive team from workspace scope — strip "Team" suffix
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  // Data Lists
  const [forms, setForms] = useState<SignupFormRecord[]>([]);
  const [templates, setTemplates] = useState<EmailTemplateRecord[]>([]);
  // Selection State
  const [selectedFormId, setSelectedFormId] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  // Content for Inspector
  const [selectedFormHtml, setSelectedFormHtml] = useState('');
  const [selectedTemplateHtml, setSelectedTemplateHtml] = useState('');
  // Conditional Rules State
  interface EmailRoutingRule {
    id: string;
    field: string;
    operator: 'equals' | 'not_equals' | 'contains';
    value: string;
    templateId: string;
  }
  const [rules, setRules] = useState<EmailRoutingRule[]>([]);
  // Confirmation page language (admin choice, baked into embed codes)
  const [confirmationLanguage, setConfirmationLanguage] = useState('');
  // UI State
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [originalFormId, setOriginalFormId] = useState(''); // To detect changes in edit mode
  // Initial Data Load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [formsData, templatesData] = await Promise.all([signupFormsApi.list(), emailTemplatesApi.list()]);
        // Filter forms and templates by team
        const teamFilteredForms = formsData.filter(f => f.Team === currentWorkspace.name || f.Team === team);
        const teamFilteredTemplates = templatesData.filter(t => t.Team === currentWorkspace.name || t.Team === team);
        setForms(teamFilteredForms);
        setTemplates(teamFilteredTemplates);
        // If editing, fetch the config record
        if (isEditing && id) {
          const config = await formConfigsApi.get(id);
          if (config) {
            const formId = extractLookupId(config.Sign_Up_Form);
            const templateId = extractLookupId(config.Email_Template);
            setSelectedFormId(formId);
            setOriginalFormId(formId);
            setSelectedTemplateId(templateId);
          } else {
            toast.error('Configuration not found');
            navigate('/configurations');
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        toast.error('Failed to load configuration data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, isEditing, navigate]);
  // Fetch Form HTML when selection changes
  useEffect(() => {
    if (!selectedFormId) {
      setSelectedFormHtml('');
      setRules([]);
      return;
    }
    // Always fetch the full record to ensure we have the latest Builder_Schema
    signupFormsApi.get(selectedFormId).then(record => {
      if (record) {
        setSelectedFormHtml(record.HTML || '');
        if (record.Builder_Schema) {
          try {
            const schema = JSON.parse(record.Builder_Schema);
            if (schema.emailRoutingRules && Array.isArray(schema.emailRoutingRules)) {
              setRules(schema.emailRoutingRules);
            } else {
              setRules([]);
            }
            setConfirmationLanguage(schema.confirmationLanguage || '');
          } catch (e) {
            setRules([]);
            setConfirmationLanguage('');
          }
        } else {
          setRules([]);
          setConfirmationLanguage('');
        }
      }
    }).catch(err => console.error('Failed to fetch form details', err));
  }, [selectedFormId]);
  // Fetch Template HTML when selection changes
  useEffect(() => {
    if (!selectedTemplateId) {
      setSelectedTemplateHtml('');
      return;
    }
    // Check if we already have the full record in the list
    const templateInList = templates.find(t => t.ID === selectedTemplateId);
    if (templateInList && templateInList.Email_HTML_Body) {
      setSelectedTemplateHtml(templateInList.Email_HTML_Body);
    } else {
      // Fetch full record if needed
      emailTemplatesApi.get(selectedTemplateId).then(record => {
        if (record) setSelectedTemplateHtml(record.Email_HTML_Body || '');
      }).catch(err => console.error('Failed to fetch template HTML', err));
    }
  }, [selectedTemplateId, templates]);
  const handleSave = async () => {
    if (!selectedFormId || !selectedTemplateId) {
      toast.error('Please select both a form and a template');
      return;
    }
    setIsSaving(true);
    try {
      // Check if this form already has a configuration with a DIFFERENT template
      const existingConfigs = await formConfigsApi.findByFormId(selectedFormId);
      const conflicting = existingConfigs.filter(c => {
        // Skip the current config if editing
        if (isEditing && id && c.ID === id) return false;
        const linkedTemplateId = extractLookupId(c.Email_Template);
        return linkedTemplateId !== selectedTemplateId;
      });
      if (conflicting.length > 0) {
        // Find the name of the already-linked template for a helpful message
        let linkedTemplateName = 'another template';
        const linkedId = extractLookupId(conflicting[0].Email_Template);
        const linkedTemplate = templates.find(t => t.ID === linkedId);
        if (linkedTemplate) {
          linkedTemplateName = `"${linkedTemplate.Email_Subject}"`;
        }
        toast.error('This form is already linked to ' + linkedTemplateName, {
          description: 'A form can only be linked to one email template. Remove the existing configuration first, or select a different form.',
          duration: 8000
        });
        setIsSaving(false);
        return;
      }
      const payload = {
        Sign_Up_Form: selectedFormId,
        Email_Template: selectedTemplateId,
        Team: team
      };
      if (isEditing && id) {
        await formConfigsApi.update(id, payload);
      } else {
        await formConfigsApi.create(payload);
      }
      // Also save the conditional rules to the SignupFormRecord's Builder_Schema
      if (rules.length > 0) {
        try {
          const formRecord = await signupFormsApi.get(selectedFormId);
          if (formRecord) {
            let schema: any = {};
            if (formRecord.Builder_Schema) {
              try {
                schema = JSON.parse(formRecord.Builder_Schema);
              } catch (e) {
                // ignore parse error
              }
            }
            schema.emailRoutingRules = rules;
            if (confirmationLanguage) {
              schema.confirmationLanguage = confirmationLanguage;
            } else {
              delete schema.confirmationLanguage;
            }
            const newSchema = JSON.stringify(schema);
            console.log('[FormConfig] Saving Builder_Schema with routing rules:', {
              formId: selectedFormId,
              rulesCount: rules.length,
              schemaLength: newSchema.length,
              rules: rules.map(r => ({
                field: r.field,
                op: r.operator,
                value: r.value,
                templateId: r.templateId
              }))
            });
            await signupFormsApi.update(selectedFormId, {
              Builder_Schema: newSchema
            });
            // ── VERIFY the save actually persisted ──
            try {
              const verifyRecord = await signupFormsApi.get(selectedFormId);
              if (verifyRecord?.Builder_Schema) {
                const savedSchema = JSON.parse(verifyRecord.Builder_Schema);
                if (savedSchema.emailRoutingRules && savedSchema.emailRoutingRules.length === rules.length) {
                  console.log('[FormConfig] ✅ Routing rules verified in Builder_Schema');
                  toast.success(`${rules.length} routing rule(s) saved to form`, {
                    duration: 3000
                  });
                } else {
                  const savedCount = savedSchema.emailRoutingRules?.length || 0;
                  console.error('[FormConfig] ❌ Rule count mismatch!', {
                    expected: rules.length,
                    saved: savedCount,
                    schemaLength: verifyRecord.Builder_Schema.length
                  });
                  toast.error(`Routing rules may not have saved correctly (expected ${rules.length}, found ${savedCount})`, {
                    description: 'The Builder_Schema field in Zoho Creator may have a character limit. Check the console for details.',
                    duration: 10000
                  });
                }
              } else {
                console.error('[FormConfig] ❌ Builder_Schema is empty after save!');
                toast.error('Builder_Schema appears empty after save — rules may have been lost', {
                  duration: 8000
                });
              }
            } catch (verifyErr) {
              console.warn('[FormConfig] Could not verify save:', verifyErr);
            }
          }
        } catch (err) {
          console.error('Failed to save routing rules to form:', err);
          toast.error('Warning: Failed to save conditional routing rules', {
            description: err instanceof Error ? err.message : 'Unknown error',
            duration: 8000
          });
        }
      } else {
        // No rules — clear any existing rules from Builder_Schema, but preserve confirmationLanguage
        try {
          const formRecord = await signupFormsApi.get(selectedFormId);
          let schema: any = {};
          if (formRecord?.Builder_Schema) {
            try {
              schema = JSON.parse(formRecord.Builder_Schema);
            } catch {
              /* ignore */
            }
          }
          let changed = false;
          if (schema.emailRoutingRules) {
            delete schema.emailRoutingRules;
            changed = true;
          }
          if (confirmationLanguage) {
            if (schema.confirmationLanguage !== confirmationLanguage) changed = true;
            schema.confirmationLanguage = confirmationLanguage;
          } else if (schema.confirmationLanguage) {
            delete schema.confirmationLanguage;
            changed = true;
          }
          if (changed) {
            await signupFormsApi.update(selectedFormId, {
              Builder_Schema: JSON.stringify(schema)
            });
          }
        } catch {
          /* ignore */
        }
      }
      // ── Refresh embed codes so deployed forms include the latest rules ──
      try {
        toast.loading('Refreshing embed codes...', {
          id: 'embed-refresh'
        });
        const refreshed = await refreshFormEmbedCodes(selectedFormId);
        toast.dismiss('embed-refresh');
        if (refreshed) {
          toast.success('Embed codes updated with routing rules', {
            description: 'The deployed form will now use the latest conditional email logic.',
            duration: 5000
          });
        } else {
          toast.warning('Could not refresh embed codes automatically', {
            description: 'Open the form editor and re-save to update embed codes.',
            duration: 8000
          });
        }
      } catch (err) {
        toast.dismiss('embed-refresh');
        console.error('[FormConfig] Embed refresh failed:', err);
        toast.warning('Embed codes not refreshed', {
          description: 'Open the form editor and re-save to update embed codes with the new rules.',
          duration: 8000
        });
      }
      toast.success(isEditing ? 'Configuration updated successfully' : 'Configuration created successfully');
      navigate('/configurations');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to save configuration', {
        description: msg
      });
    } finally {
      setIsSaving(false);
    }
  };
  const handleCopyVariable = (name: string) => {
    const variable = `{{${name}}}`;
    copyToClipboard(variable).then(() => toast.success(`Copied ${variable}`)).catch(() => toast.error('Failed to copy'));
  };
  // --- Render Helpers ---
  const formFields = selectedFormHtml ? extractFormFields(selectedFormHtml) : [];
  const hasInspector = selectedFormId && selectedTemplateId;
  const isVariableInTemplate = (varName: string) => {
    if (!selectedTemplateHtml) return false;
    return selectedTemplateHtml.includes(`{{${varName}}}`);
  };
  if (loading) {
    return <div className="flex items-center justify-center py-24" data-id="element-4264">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-4265" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-4266">
          Loading configuration...
        </span>
      </div>;
  }
  return <div className="space-y-6 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4267">
      {/* Header */}
      <div className="flex items-center gap-4" data-id="element-4268">
        <Link to="/configurations" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-4269">
          <ArrowLeft className="h-5 w-5" data-id="element-4270" />
        </Link>
        <div data-id="element-4271">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-4272">
            {isEditing ? 'Edit Configuration' : 'New Configuration'}
          </h1>
          <p className="text-sm text-gray-500" data-id="element-4273">
            Link a signup form to an email template and verify variables.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start" data-id="element-4274">
        {/* Left Column: Form Fields */}
        <div className="lg:col-span-1 space-y-6" data-id="element-4275">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-6" data-id="element-4276">
            {/* Form Selection */}
            <div className="space-y-2" data-id="element-4277">
              <label className="block text-sm font-medium text-gray-700" data-id="element-4278">
                Sign Up Form <span className="text-red-500" data-id="element-4279">*</span>
              </label>
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={selectedFormId} onChange={e => setSelectedFormId(e.target.value)} data-id="element-4280">
                <option value="" data-id="element-4281">Select a form...</option>
                {forms.map(form => <option key={form.ID} value={form.ID} data-id="element-4282">
                    {form.Form_Name}
                  </option>)}
              </select>
              <p className="text-xs text-gray-500" data-id="element-4283">
                The form that triggers this configuration.
              </p>
            </div>

            {/* Change Warning */}
            {isEditing && selectedFormId !== originalFormId && <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex gap-2" data-id="element-4284">
                <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-4285" />
                <div className="text-xs text-amber-800" data-id="element-4286">
                  <span className="font-medium" data-id="element-4287">Form changed.</span> Check the
                  variable inspector to ensure the template supports the new
                  form's fields.
                </div>
              </div>}

            {/* Template Selection */}
            <div className="space-y-2" data-id="element-4288">
              <label className="block text-sm font-medium text-gray-700" data-id="element-4289">
                Default Email Template{' '}
                <span className="text-gray-400 font-normal" data-id="element-4290">(Optional)</span>
              </label>
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={selectedTemplateId} onChange={e => setSelectedTemplateId(e.target.value)} data-id="element-4291">
                <option value="" data-id="element-4292">None (No confirmation email)</option>
                {templates.map(template => <option key={template.ID} value={template.ID} data-id="element-4293">
                    {template.Email_Subject}
                    {templates.filter(t => t.Email_Subject === template.Email_Subject).length > 1 ? ` (ID: ${template.ID})` : ''}
                  </option>)}
              </select>
              <p className="text-xs text-gray-500" data-id="element-4294">
                The fallback template used if no conditional rules match.
              </p>
            </div>

            {/* Conditional Email Rules */}
            <div className="space-y-3 pt-4 border-t border-gray-200" data-id="element-4295">
              <div className="flex items-center justify-between" data-id="element-4296">
                <label className="block text-sm font-medium text-gray-700" data-id="element-4297">
                  Conditional Email Routing
                </label>
                <button type="button" onClick={() => {
                setRules([...rules, {
                  id: Date.now().toString(),
                  field: formFields[0] || '',
                  operator: 'equals',
                  value: '',
                  templateId: ''
                }]);
              }} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-id="element-4298">
                  + Add Rule
                </button>
              </div>
              <p className="text-xs text-gray-500" data-id="element-4299">
                Send different templates based on form field values. Rules are
                evaluated in order.
              </p>

              {rules.length > 0 && <div className="space-y-3" data-id="element-4300">
                  {rules.map((rule, index) => <div key={rule.id} className="p-3 bg-gray-50 border border-gray-200 rounded-md space-y-3 relative" data-id="element-4301">
                      <button type="button" onClick={() => setRules(rules.filter(r => r.id !== rule.id))} className="absolute top-2 right-2 text-gray-400 hover:text-red-500" data-id="element-4302">
                        &times;
                      </button>
                      <div className="text-xs font-medium text-gray-500 uppercase" data-id="element-4303">
                        Rule {index + 1}
                      </div>

                      <div className="grid grid-cols-3 gap-2" data-id="element-4304">
                        <div className="col-span-1" data-id="element-4305">
                          <label className="block text-[10px] text-gray-500 mb-1" data-id="element-4306">
                            Field
                          </label>
                          <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.field} onChange={e => {
                      const newRules = [...rules];
                      newRules[index].field = e.target.value;
                      setRules(newRules);
                    }} data-id="element-4307">
                            <option value="" data-id="element-4308">Select field...</option>
                            {formFields.map(f => <option key={f} value={f} data-id="element-4309">
                                {f}
                              </option>)}
                          </select>
                        </div>
                        <div className="col-span-1" data-id="element-4310">
                          <label className="block text-[10px] text-gray-500 mb-1" data-id="element-4311">
                            Condition
                          </label>
                          <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.operator} onChange={e => {
                      const newRules = [...rules];
                      newRules[index].operator = e.target.value as any;
                      setRules(newRules);
                    }} data-id="element-4312">
                            <option value="equals" data-id="element-4313">Equals</option>
                            <option value="not_equals" data-id="element-4314">Not Equals</option>
                            <option value="contains" data-id="element-4315">Contains</option>
                          </select>
                        </div>
                        <div className="col-span-1" data-id="element-4316">
                          <label className="block text-[10px] text-gray-500 mb-1" data-id="element-4317">
                            Value
                          </label>
                          <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.value} onChange={e => {
                      const newRules = [...rules];
                      newRules[index].value = e.target.value;
                      setRules(newRules);
                    }} placeholder="e.g. Male" data-id="element-4318" />
                        </div>
                      </div>

                      <div data-id="element-4319">
                        <label className="block text-[10px] text-gray-500 mb-1" data-id="element-4320">
                          Send Template
                        </label>
                        <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.templateId} onChange={e => {
                    const newRules = [...rules];
                    newRules[index].templateId = e.target.value;
                    setRules(newRules);
                  }} data-id="element-4321">
                          <option value="" data-id="element-4322">Select template...</option>
                          {templates.map(template => <option key={template.ID} value={template.ID} data-id="element-4323">
                              {template.Email_Subject}
                              {templates.filter(t => t.Email_Subject === template.Email_Subject).length > 1 ? ` (ID: ${template.ID})` : ''}
                            </option>)}
                        </select>
                      </div>
                    </div>)}
                </div>}
            </div>

            {/* Confirmation Page Language */}
            <div className="space-y-2 pt-4 border-t border-gray-200" data-id="element-4324">
              <label className="block text-sm font-medium text-gray-700" data-id="element-4325">
                Confirmation Page Language
              </label>
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={confirmationLanguage} onChange={e => setConfirmationLanguage(e.target.value)} data-id="element-4326">
                <option value="" data-id="element-4327">English (Default)</option>
                <option value="pt-BR" data-id="element-4328">🇧🇷 Português (Brasil)</option>
                <option value="es" data-id="element-4329">🇪🇸 Español</option>
                <option value="fr" data-id="element-4330">🇫🇷 Français</option>
                <option value="de" data-id="element-4331">🇩🇪 Deutsch</option>
                <option value="it" data-id="element-4332">🇮🇹 Italiano</option>
                <option value="ar" data-id="element-4333">🇸🇦 العربية</option>
                <option value="tr" data-id="element-4334">🇹🇷 Türkçe</option>
                <option value="nl" data-id="element-4335">🇳🇱 Nederlands</option>
                <option value="pl" data-id="element-4336">🇵🇱 Polski</option>
                <option value="ru" data-id="element-4337">🇷🇺 Русский</option>
                <option value="zh" data-id="element-4338">🇨🇳 中文</option>
                <option value="ja" data-id="element-4339">🇯🇵 日本語</option>
                <option value="ko" data-id="element-4340">🇰🇷 한국어</option>
                <option value="hi" data-id="element-4341">🇮🇳 हिन्दी</option>
                <option value="th" data-id="element-4342">🇹🇭 ไทย</option>
                <option value="vi" data-id="element-4343">🇻🇳 Tiếng Việt</option>
                <option value="id" data-id="element-4344">🇮🇩 Bahasa Indonesia</option>
                <option value="ms" data-id="element-4345">🇲🇾 Bahasa Melayu</option>
                <option value="tl" data-id="element-4346">🇵🇭 Tagalog</option>
              </select>
              <p className="text-xs text-gray-500" data-id="element-4347">
                The language shown on the subscription confirmation page when
                subscribers click the email link.
              </p>
            </div>

            {/* Team Field */}
            <div className="space-y-2" data-id="element-4348">
              <label className="block text-sm font-medium text-gray-700" data-id="element-4349">
                Team
              </label>
              <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-4350" />
              <p className="text-xs text-gray-400" data-id="element-4351">
                Automatically set from your current workspace scope
              </p>
            </div>

            {/* Save Button */}
            <div className="pt-4" data-id="element-4352">
              <button onClick={handleSave} disabled={isSaving || !selectedFormId} className="w-full inline-flex justify-center items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-4353">
                {isSaving ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-4354" /> : <Save className="h-4 w-4" data-id="element-4355" />}
                {isSaving ? 'Saving...' : isEditing ? 'Update Configuration' : 'Create Configuration'}
              </button>
            </div>
          </div>
        </div>

        {/* Right Column: Variable Inspector */}
        <div className="lg:col-span-2" data-id="element-4356">
          {hasInspector ? <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-4357">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-4358">
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-4359">
                  <Info className="h-4 w-4 text-blue-500" data-id="element-4360" />
                  Variable Inspector
                </h3>
                {selectedTemplateId && <Link to={`/templates/${selectedTemplateId}`} target="_blank" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1" data-id="element-4361">
                    Edit Template <ExternalLink className="h-3 w-3" data-id="element-4362" />
                  </Link>}
              </div>

              <div className="p-6 space-y-8" data-id="element-4363">
                {/* System Variables */}
                <div className="space-y-3" data-id="element-4364">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4365">
                    System Variables
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-4366">
                    {['confirmation_link', 'team_name'].map(sysVar => {
                  const found = isVariableInTemplate(sysVar);
                  return <button key={sysVar} onClick={() => handleCopyVariable(sysVar)} className={`flex items-center justify-between p-3 rounded-md border text-left transition-all ${found ? 'bg-green-50 border-green-200 hover:border-green-300' : 'bg-amber-50 border-amber-200 hover:border-amber-300'}`} data-id="element-4367">
                          <div className="flex items-center gap-2" data-id="element-4368">
                            <code className={`text-sm font-medium ${found ? 'text-green-700' : 'text-amber-700'}`} data-id="element-4369">
                              {`{{${sysVar}}}`}
                            </code>
                          </div>
                          {found ? <div className="flex items-center gap-1 text-xs text-green-600 font-medium" data-id="element-4370">
                              <Check className="h-3 w-3" data-id="element-4371" /> Found
                            </div> : <div className="flex items-center gap-1 text-xs text-amber-600 font-medium" data-id="element-4372">
                              <AlertTriangle className="h-3 w-3" data-id="element-4373" /> Missing
                            </div>}
                        </button>;
                })}
                  </div>
                  <p className="text-xs text-gray-500" data-id="element-4374">
                    <span className="font-medium" data-id="element-4375">Note:</span>{' '}
                    <code data-id="element-4376">{`{{confirmation_link}}`}</code> is required for
                    double opt-in.
                  </p>
                </div>

                {/* Form Variables */}
                <div className="space-y-3" data-id="element-4377">
                  <div className="flex items-center justify-between" data-id="element-4378">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-4379">
                      Form Variables
                    </h4>
                    <span className="text-xs text-gray-400" data-id="element-4380">
                      {formFields.length} fields found in form
                    </span>
                  </div>

                  {formFields.length === 0 ? <div className="text-sm text-gray-500 italic bg-gray-50 p-4 rounded border border-dashed border-gray-200" data-id="element-4381">
                      No input fields found in the selected form's HTML.
                    </div> : <div className="flex flex-wrap gap-2" data-id="element-4382">
                      {formFields.map(field => {
                  const found = isVariableInTemplate(field);
                  return <button key={field} onClick={() => handleCopyVariable(field)} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors ${found ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'}`} title={found ? 'Found in template' : 'Not used in template (click to copy)'} data-id="element-4383">
                            <code data-id="element-4384">{field}</code>
                            {found && <Check className="h-3 w-3" data-id="element-4385" />}
                          </button>;
                })}
                    </div>}
                  <p className="text-xs text-gray-500" data-id="element-4386">
                    Green variables are currently used in the selected template.
                    Gray ones are available but unused.
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200" data-id="element-4387">
                <p className="text-xs text-gray-500 flex items-start gap-2" data-id="element-4388">
                  <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-4389" />
                  If you need to add variables to the template, click "Edit
                  Template" above. The variables will work automatically once
                  added to the HTML body.
                </p>
              </div>
            </div> : <div className="h-full min-h-[300px] flex flex-col items-center justify-center text-center p-8 bg-gray-50 border border-gray-200 border-dashed rounded-lg" data-id="element-4390">
              <div className="bg-white p-3 rounded-full shadow-sm mb-4" data-id="element-4391">
                <Info className="h-6 w-6 text-gray-400" data-id="element-4392" />
              </div>
              <h3 className="text-sm font-medium text-gray-900" data-id="element-4393">
                Variable Inspector
              </h3>
              <p className="text-sm text-gray-500 mt-1 max-w-xs" data-id="element-4394">
                Select both a form and a template to see which variables are
                being used and which are missing. Email templates are optional.
              </p>
            </div>}
        </div>
      </div>
    </div>;
}